'use client'

import { useState, useEffect, useMemo, useRef } from 'react'
import Link from 'next/link'
import { useRouter, usePathname } from 'next/navigation'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { ThemeToggle } from '@/components/theme-toggle'
import { useAuth } from '@/contexts/auth-context'
import { shopsApi, type Shop } from '@/lib/api/shops'
import { SubscriptionStatus } from '@/components/subscription-status'
import { NotificationsBell } from '@/components/notifications-bell'
import {
  Store,
  Plus,
  Loader2,
  LogOut,
  User,
  MessageCircle,
  ExternalLink,
  Settings,
  ChevronDown,
  Menu,
  CreditCard,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { LOGO_SRC } from '@/lib/brand'
import { filterShopsForSelector, isDemoShop } from '@/lib/is-demo-shop'
import { canAccessPlanFact } from '@/lib/plan-fact/access'
import { SUPPORT_TELEGRAM_URL } from '@/lib/shop-subscription'

type BreadcrumbSegment = 'table' | 'plan-fact' | 'settings' | 'payment' | 'admin' | 'home'

/** Текущий раздел для подсветки в цепочке «таблица РНП / …». */
function getActiveBreadcrumbSegment(pathname: string | null): BreadcrumbSegment {
  if (!pathname) return 'table'
  if (pathname === '/') return 'home'
  if (pathname.startsWith('/admin')) return 'admin'
  if (pathname.includes('/payment')) return 'payment'
  if (pathname.includes('/plan-fact') && pathname.startsWith('/rnp/')) return 'plan-fact'
  if (pathname.includes('/settings') && pathname.startsWith('/rnp/')) return 'settings'
  return 'table'
}

type HeaderMode = 'home' | 'service' | 'settings' | 'payment'

interface HeaderProps {
  mode?: HeaderMode
  shopId?: string
  currentShopName?: string
  shops?: Shop[]
  isLoadingShops?: boolean
  onShopUpdate?: (shop: Shop) => void
}

export function Header({ 
  mode = 'home', 
  shopId,
  currentShopName,
  shops: externalShops,
  isLoadingShops: externalIsLoadingShops,
  onShopUpdate,
}: HeaderProps) {
  const router = useRouter()
  const pathname = usePathname()
  const { isAuthenticated, user, logout } = useAuth()
  const [shops, setShops] = useState<Shop[]>(externalShops || [])
  const [isLoadingShops, setIsLoadingShops] = useState(externalIsLoadingShops || false)
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)

  // Автоопределение режима из pathname
  const actualMode: HeaderMode = mode || (
    pathname?.includes('/payment') ? 'payment' :
    pathname?.includes('/settings') ? 'settings' :
    pathname?.startsWith('/rnp/') && pathname !== '/rnp' ? 'service' :
    'home'
  )

  // Загрузка магазинов для режима service/settings/payment/home
  useEffect(() => {
    if ((actualMode === 'service' || actualMode === 'settings' || actualMode === 'payment' || actualMode === 'home') && !externalShops && isAuthenticated) {
      // Сначала загружаем из кеша для мгновенного отображения
      let hasCachedData = false
      if (typeof window !== 'undefined') {
        const cachedShops = localStorage.getItem('cached_shops')
        if (cachedShops) {
          try {
            const parsed = JSON.parse(cachedShops)
            // Проверяем, что кеш не старше 5 минут
            const cacheTime = localStorage.getItem('cached_shops_time')
            if (cacheTime && Date.now() - parseInt(cacheTime) < 5 * 60 * 1000) {
              setShops(parsed)
              hasCachedData = true
            }
          } catch (e) {
            console.error('Failed to parse cached shops:', e)
          }
        }
      }

      const loadShops = async () => {
        // Не показываем загрузку, если уже есть кешированные данные
        if (!hasCachedData) {
          setIsLoadingShops(true)
        }
        try {
          const shopsList = await shopsApi.listMyShops()
          setShops(shopsList || [])
          // Сохраняем в кеш
          if (typeof window !== 'undefined') {
            localStorage.setItem('cached_shops', JSON.stringify(shopsList || []))
            localStorage.setItem('cached_shops_time', String(Date.now()))
          }
        } catch (error: any) {
          console.error('Failed to load shops:', error)
          // Не показываем ошибку, если есть кешированные данные
          if (!hasCachedData) {
            toast.error('Ошибка загрузки магазинов', {
        duration: Infinity,
      })
          }
        } finally {
          setIsLoadingShops(false)
        }
      }
      loadShops()
    } else if (externalShops) {
      setShops(externalShops)
      // Обновляем кеш при получении внешних данных
      if (typeof window !== 'undefined') {
        localStorage.setItem('cached_shops', JSON.stringify(externalShops))
        localStorage.setItem('cached_shops_time', String(Date.now()))
      }
    }
  }, [actualMode, externalShops, isAuthenticated])

  // Синхронизация isLoadingShops с внешним пропсом
  useEffect(() => {
    if (externalIsLoadingShops !== undefined) {
      setIsLoadingShops(externalIsLoadingShops)
    }
  }, [externalIsLoadingShops])

  // Определяем имя текущего магазина
  const getCurrentShopName = () => {
    if (currentShopName) return currentShopName
    if (shopId && shops.length > 0) {
      const shop = shops.find(s => s.id === shopId)
      return shop?.name || shop?.sid || 'Магазин'
    }
    return 'Магазин'
  }

  const handleShopChange = (newShopId: string) => {
    // Сохраняем выбранный магазин в localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('selected_shop_id', newShopId)
    }
    
    // Определяем текущий путь и сохраняем его структуру
    if (pathname) {
      // Если мы на странице настроек, остаемся в настройках
      if (pathname.includes('/settings')) {
        router.push(`/rnp/${newShopId}/settings`)
      }
      // Если мы на странице оплаты, остаемся в оплате
      else if (pathname.includes('/payment')) {
        router.push(`/rnp/${newShopId}/payment`)
      }
      // Иначе переходим на главную страницу магазина
      else {
        router.push(`/rnp/${newShopId}`)
      }
    } else {
      router.push(`/rnp/${newShopId}`)
    }
  }

  const handleLogout = async () => {
    await logout()
    router.push('/')
    toast.success('Выход выполнен')
  }

  const openAddShopDialog = async () => {
    if (user?.role === 'guest') {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('selected_shop_id')
        localStorage.removeItem('cached_shops')
        localStorage.removeItem('cached_shops_time')
        window.location.replace('/auth/register')
        return
      }
      router.push('/auth/register')
      toast.message('Создайте аккаунт, чтобы подключить свой магазин')
      return
    }

    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.set('add', 'true')
      router.push(currentUrl.pathname + currentUrl.search)
      return
    }

    if (pathname) {
      router.push(`${pathname}?add=true`)
      return
    }

    router.push('/rnp?add=true')
  }

  const isServiceMode = actualMode === 'service'
  const isHomeMode = actualMode === 'home'
  const isSettingsMode = actualMode === 'settings'
  const isPaymentMode = actualMode === 'payment'
  const isAdmin = user?.role === 'admin'
  const isGuestUser = user?.role === 'guest'
  const hasOnlyDemoShop = shops.length > 0 && shops.every((shop) => isDemoShop(shop))

  // Определяем текущий shopId для режима home (из localStorage или первого магазина)
  const getCurrentShopIdForHome = () => {
    if (shopId) return shopId
    if (isHomeMode && shops.length > 0) {
      // Пытаемся взять из localStorage
      if (typeof window !== 'undefined') {
        const savedShopId = localStorage.getItem('selected_shop_id')
        if (savedShopId && shops.find(s => s.id === savedShopId)) {
          return savedShopId
        }
      }
      // Иначе берем первый магазин
      return shops[0].id
    }
    return null
  }

  const currentShopIdForHome = getCurrentShopIdForHome()

  const resolvedShopId = useMemo(() => {
    if (shopId) return shopId
    const fromPath = pathname?.match(/^\/rnp\/([^/]+)/)?.[1]
    if (fromPath) return fromPath
    return currentShopIdForHome
  }, [shopId, pathname, currentShopIdForHome])

  const isAdminPath = Boolean(pathname?.startsWith('/admin'))
  const activeSegment = getActiveBreadcrumbSegment(pathname ?? null)

  const breadcrumbItems = useMemo(() => {
    if (!isAuthenticated) return []

    const tableHref = resolvedShopId ? `/rnp/${resolvedShopId}` : '/rnp'
    const items: Array<{ label: string; href: string; segment: BreadcrumbSegment }> = [
      { label: 'таблица РНП', href: tableHref, segment: 'table' },
    ]

    if (resolvedShopId) {
      // Админ — всегда; для allowlist-магазинов — все с доступом к магазину.
      if (canAccessPlanFact(resolvedShopId, { isAdmin })) {
        items.push({
          label: 'план/факт',
          href: `/rnp/${resolvedShopId}/plan-fact`,
          segment: 'plan-fact',
        })
      }
      items.push({
        label: 'настройки',
        href: `/rnp/${resolvedShopId}/settings`,
        segment: 'settings',
      })
    }

    if (resolvedShopId && activeSegment === 'payment') {
      items.push({
        label: 'оплата',
        href: `/rnp/${resolvedShopId}/payment`,
        segment: 'payment',
      })
    }

    if (isAdmin) {
      items.push({ label: 'админка', href: '/admin', segment: 'admin' })
    }

    if (isGuestUser) {
      items.push({ label: 'на главную', href: '/', segment: 'home' })
    }

    return items
  }, [isAuthenticated, resolvedShopId, activeSegment, isAdmin, isGuestUser])

  const showBreadcrumbNav =
    breadcrumbItems.length > 0 &&
    (isServiceMode ||
      isSettingsMode ||
      isPaymentMode ||
      isAdminPath ||
      (isHomeMode && (shops.length > 0 || Boolean(resolvedShopId))))

  const getShopDisplayName = (shop: Shop) => shop.name || shop.sid || 'Магазин'
  const isBrandshop = (shop: Shop) => shop.shop_kind === 'brandshop'
  const hasActiveSubscription = (shop: Shop) => shop.subscription?.status === 'active'

  const selectableShops = useMemo(
    () => filterShopsForSelector(shops, isAdmin),
    [shops, isAdmin],
  )

  const sortedShops = useMemo(() => {
    return [...selectableShops].sort((a, b) => {
      const aHasActive = hasActiveSubscription(a)
      const bHasActive = hasActiveSubscription(b)
      const aIsBrandshop = a.shop_kind === 'brandshop'
      const bIsBrandshop = b.shop_kind === 'brandshop'

      const getPriority = (isActive: boolean, isBrandshop: boolean): number => {
        if (isActive && !isBrandshop) return 0
        if (isActive && isBrandshop) return 1
        return 2
      }

      const priorityDiff = getPriority(aHasActive, aIsBrandshop) - getPriority(bHasActive, bIsBrandshop)
      if (priorityDiff !== 0) return priorityDiff

      return getShopDisplayName(a).localeCompare(getShopDisplayName(b), 'ru', { sensitivity: 'base' })
    })
  }, [selectableShops])

  /** Единые стили кнопок в шапке: «чип», как выпадающий магазин в кабинетах маркетплейсов */
  const chipButton =
    'inline-flex items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted/70 disabled:pointer-events-none disabled:opacity-50'

  const showShopSelector =
    (isServiceMode || isSettingsMode || isPaymentMode) ||
    (isHomeMode && shops.length > 0) ||
    isAdminPath ||
    Boolean(resolvedShopId)

  const subscriptionShopId = shopId || currentShopIdForHome || resolvedShopId || ''
  const subscriptionShop = shops.find((s) => s.id === subscriptionShopId) || null
  const currentShopForBadge = useMemo(() => {
    const candidateIds = [
      subscriptionShopId,
      resolvedShopId,
      shopId,
      currentShopIdForHome,
    ].filter((value): value is string => Boolean(value))

    for (const candidateId of candidateIds) {
      const matched = shops.find((shop) => shop.id === candidateId)
      if (matched) return matched
    }

    return null
  }, [subscriptionShopId, resolvedShopId, shopId, currentShopIdForHome, shops])
  const shouldShowDemoShopBadge =
    isAuthenticated && (hasOnlyDemoShop || isDemoShop(currentShopForBadge))
  const showSubscriptionStatus =
    !isGuestUser &&
    Boolean(
      subscriptionShopId &&
        (((isServiceMode || isSettingsMode || isPaymentMode) && shopId) ||
          (isHomeMode && currentShopIdForHome) ||
          resolvedShopId),
    )

  const closeMobileSheet = () => setIsMobileSheetOpen(false)

  const handleMobileShopSelect = (newShopId: string) => {
    if (isHomeMode && !shopId) {
      if (typeof window !== 'undefined') {
        localStorage.setItem('selected_shop_id', newShopId)
      }
      closeMobileSheet()
      window.location.reload()
      return
    }
    handleShopChange(newShopId)
    closeMobileSheet()
  }

  const renderBreadcrumbs = (className?: string) => {
    if (breadcrumbItems.length === 0) return null
    return (
      <nav
        aria-label="Путь по разделам"
        className={cn(
          'flex items-center gap-x-1.5 gap-y-0.5 text-sm min-w-0',
          className,
        )}
      >
        {breadcrumbItems.map((item, index) => {
          const isActive = item.segment === activeSegment
          return (
            <span key={item.href} className="inline-flex items-center gap-1.5 shrink-0">
              {index > 0 && (
                <span className="text-muted-foreground/40 select-none" aria-hidden>
                  /
                </span>
              )}
              {isActive ? (
                <span className="font-medium text-primary">{item.label}</span>
              ) : (
                <Link
                  href={item.href}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                  onClick={() => setIsMobileSheetOpen(false)}
                >
                  {item.label}
                </Link>
              )}
            </span>
          )
        })}
      </nav>
    )
  }

  return (
    <header
      ref={headerRef}
      className="app-top-header sticky top-0 z-50 shrink-0 border-b border-border bg-card text-card-foreground shadow-[0_1px_3px_-1px_rgba(15,23,42,0.08)] supports-[backdrop-filter]:backdrop-blur-0 dark:shadow-[0_1px_3px_-1px_rgba(0,0,0,0.35)]"
    >
      <div className="w-full px-4 py-2">
        <div className="flex items-center justify-between gap-4 min-h-10">
          {/* Логотип */}
          <Link 
            href={isAuthenticated ? "/rnp" : "/"} 
            className="flex items-center gap-2 rnp-logo shrink-0 rounded-lg pr-2 py-0.5 -ml-1 hover:bg-muted/40 transition-colors"
          >
            <img 
              src={LOGO_SRC} 
              alt="РНП" 
              className="h-8 w-auto"
            />
            <div className="flex flex-col leading-tight">
              <span className="text-[15px] font-semibold text-foreground tracking-tight">РНП</span>
              <span className="text-[10px] text-muted-foreground leading-none uppercase tracking-wide">rnpwb.ru</span>
            </div>
          </Link>

          {/* Центральная область - для service/settings/payment и home (если есть магазины или навигация) */}
          {showBreadcrumbNav && (
            <div className="flex-1 flex items-center justify-center gap-4 max-md:hidden">
              {/* Выбор магазина - для service/settings/payment и home (если есть магазины) */}
              {showShopSelector && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      chipButton,
                      'max-w-[min(320px,40vw)]',
                      hasOnlyDemoShop && 'border-primary/50 bg-primary/10 text-primary hover:bg-primary/15',
                    )}
                    disabled={isLoadingShops}
                    onClick={(event) => {
                      if (!hasOnlyDemoShop) return
                      event.preventDefault()
                      void openAddShopDialog()
                    }}
                  >
                    {isLoadingShops ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                    ) : hasOnlyDemoShop ? (
                      <Plus className="h-4 w-4 shrink-0 text-primary" />
                    ) : (
                      <Store className="h-4 w-4 shrink-0 text-primary" />
                    )}
                    <span className="truncate">
                      {hasOnlyDemoShop
                        ? 'Подключиться к РНП'
                        : (isHomeMode && !shopId
                            ? (shops.find(s => s.id === currentShopIdForHome)?.name || shops.find(s => s.id === currentShopIdForHome)?.sid || 'Магазин')
                            : getCurrentShopName())}
                    </span>
                    {!isLoadingShops && !hasOnlyDemoShop && (
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="min-w-[200px]">
                  {!hasOnlyDemoShop && sortedShops.map((shop) => {
                    const isSelected = shopId === shop.id || (isHomeMode && !shopId && currentShopIdForHome === shop.id)
                    const isActiveSubscription = hasActiveSubscription(shop)
                    return (
                      <DropdownMenuItem
                        key={shop.id}
                        onClick={() => {
                          if (isHomeMode && !shopId) {
                            // Для режима home сохраняем выбранный магазин в localStorage
                            if (typeof window !== 'undefined') {
                              localStorage.setItem('selected_shop_id', shop.id)
                            }
                            // Обновляем страницу для применения изменений
                            window.location.reload()
                          } else {
                            handleShopChange(shop.id)
                          }
                        }}
                        className={cn(
                          isSelected ? 'bg-muted/40 hover:bg-muted/60' : 'hover:bg-muted/50'
                        )}
                      >
                        <Store className="h-4 w-4" />
                        <span className="flex-1 truncate">
                          {getShopDisplayName(shop)}
                          {isBrandshop(shop) && <span className="text-muted-foreground/50"> (Б)</span>}
                        </span>
                        <span
                          className={cn(
                            'h-2 w-2 rounded-full',
                            isActiveSubscription ? 'bg-emerald-400/80' : 'bg-rose-400/80'
                          )}
                          title={isActiveSubscription ? 'Подписка активна' : 'Подписка неактивна'}
                        />
                      </DropdownMenuItem>
                    )
                  })}
                  <DropdownMenuItem
                    onClick={() => {
                      void openAddShopDialog()
                    }}
                    className="border-t mt-1 pt-1"
                  >
                    <Plus className="h-4 w-4" />
                    {hasOnlyDemoShop ? 'Подключиться к РНП' : 'Добавить магазин'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              )}

              {renderBreadcrumbs('flex-wrap')}
            </div>
          )}

          {/* Правая область */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {shouldShowDemoShopBadge && (
              <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] font-medium leading-none text-amber-700 dark:text-amber-300">
                Демо магазин
              </span>
            )}
            {isAuthenticated ? (
              <>
                {/* Мобильное меню: все действия хедера в одной панели */}
                <Sheet open={isMobileSheetOpen} onOpenChange={setIsMobileSheetOpen}>
                  <SheetTrigger asChild>
                    <button
                      type="button"
                      className={cn(chipButton, 'md:hidden')}
                      aria-label="Открыть меню"
                    >
                      <Menu className="h-4 w-4 shrink-0" />
                      <span>Меню</span>
                    </button>
                  </SheetTrigger>
                  <SheetContent side="right" className="flex w-[min(100vw,20rem)] flex-col gap-0 p-0">
                    <SheetHeader className="border-b border-border px-4 py-3 text-left">
                      <SheetTitle>Меню</SheetTitle>
                    </SheetHeader>
                    <ScrollArea className="flex-1 px-2">
                      <div className="space-y-1 py-2">
                        {showShopSelector && (
                          <section className="px-2 pb-2">
                            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Магазин
                            </p>
                            <p className="mb-2 truncate text-sm font-medium text-foreground">
                              {hasOnlyDemoShop
                                ? 'Демо-магазин'
                                : getCurrentShopName()}
                            </p>
                            {shouldShowDemoShopBadge && (
                              <span className="mb-2 inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] font-medium leading-none text-amber-700 dark:text-amber-300">
                                Демо магазин
                              </span>
                            )}
                            {!hasOnlyDemoShop &&
                              sortedShops.map((shop) => {
                                const isSelected =
                                  shopId === shop.id ||
                                  (isHomeMode && !shopId && currentShopIdForHome === shop.id)
                                const isActiveSubscription = hasActiveSubscription(shop)
                                return (
                                  <button
                                    key={shop.id}
                                    type="button"
                                    onClick={() => handleMobileShopSelect(shop.id)}
                                    className={cn(
                                      'flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
                                      isSelected
                                        ? 'bg-muted font-medium'
                                        : 'hover:bg-muted/60',
                                    )}
                                  >
                                    <Store className="h-4 w-4 shrink-0 text-primary" />
                                    <span className="flex-1 truncate">
                                      {getShopDisplayName(shop)}
                                      {isBrandshop(shop) && (
                                        <span className="text-muted-foreground/50"> (Б)</span>
                                      )}
                                    </span>
                                    <span
                                      className={cn(
                                        'h-2 w-2 shrink-0 rounded-full',
                                        isActiveSubscription
                                          ? 'bg-emerald-400/80'
                                          : 'bg-rose-400/80',
                                      )}
                                    />
                                  </button>
                                )
                              })}
                            <button
                              type="button"
                              onClick={() => {
                                void openAddShopDialog()
                                closeMobileSheet()
                              }}
                              className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-muted/60"
                            >
                              <Plus className="h-4 w-4 shrink-0 text-primary" />
                              {hasOnlyDemoShop ? 'Подключиться к РНП' : 'Добавить магазин'}
                            </button>
                          </section>
                        )}

                        {showBreadcrumbNav && breadcrumbItems.length > 0 && (
                          <section className="border-t border-border px-2 py-2">
                            <p className="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Разделы
                            </p>
                            {breadcrumbItems.map((item) => {
                              const isActive = item.segment === activeSegment
                              return isActive ? (
                                <div
                                  key={item.href}
                                  className="rounded-lg px-3 py-2.5 text-sm font-medium text-primary"
                                >
                                  {item.label}
                                </div>
                              ) : (
                                <Link
                                  key={item.href}
                                  href={item.href}
                                  onClick={closeMobileSheet}
                                  className="block rounded-lg px-3 py-2.5 text-sm text-foreground hover:bg-muted/60"
                                >
                                  {item.label}
                                </Link>
                              )
                            })}
                          </section>
                        )}

                        {showSubscriptionStatus && (
                          <section className="border-t border-border px-4 py-3">
                            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Подписка
                            </p>
                            <SubscriptionStatus
                              shop={subscriptionShop}
                              shopId={subscriptionShopId}
                            />
                          </section>
                        )}

                        {!isGuestUser && (
                          <section className="border-t border-border px-2 py-2">
                            <p className="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Аккаунт
                            </p>
                            {user && (
                              <p className="mb-2 px-3 text-xs text-muted-foreground">
                                {user.email ||
                                  (user.telegram_username
                                    ? `@${user.telegram_username}`
                                    : 'Нет контакта')}
                                {user.name ? ` (${user.name})` : ''}
                              </p>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                router.push('/settings')
                                closeMobileSheet()
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-muted/60"
                            >
                              <Settings className="h-4 w-4" />
                              Настройки профиля
                            </button>
                            <button
                              type="button"
                              onClick={() => window.open(SUPPORT_TELEGRAM_URL, '_blank')}
                              className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-muted/60"
                            >
                              <MessageCircle className="h-4 w-4" />
                              Поддержка
                            </button>
                            <button
                              type="button"
                              onClick={() => window.open('https://t.me/rnp_4wb', '_blank')}
                              className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-muted/60"
                            >
                              <ExternalLink className="h-4 w-4" />
                              Наш канал
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                void handleLogout()
                                closeMobileSheet()
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-destructive hover:bg-destructive/10"
                            >
                              <LogOut className="h-4 w-4" />
                              Выйти
                            </button>
                          </section>
                        )}

                        {showBreadcrumbNav &&
                          resolvedShopId &&
                          !isGuestUser && (
                            <section className="border-t border-border px-2 py-2">
                              <button
                                type="button"
                                onClick={() => {
                                  router.push(`/rnp/${resolvedShopId}/payment`)
                                  closeMobileSheet()
                                }}
                                className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-muted/60"
                              >
                                <CreditCard className="h-4 w-4" />
                                Оплата подписки
                              </button>
                            </section>
                          )}
                      </div>
                    </ScrollArea>
                    <div className="flex items-center justify-between border-t border-border px-4 py-3">
                      <span className="text-sm text-muted-foreground">Тема</span>
                      <ThemeToggle />
                    </div>
                  </SheetContent>
                </Sheet>

                {showSubscriptionStatus && (
                  <div className="max-md:hidden">
                    <SubscriptionStatus shop={subscriptionShop} shopId={subscriptionShopId} />
                  </div>
                )}

                {!isGuestUser && (
                  <>
                    <NotificationsBell />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            chipButton,
                            'hidden md:inline-flex text-muted-foreground hover:text-foreground max-w-[160px]',
                          )}
                        >
                          <User className="h-4 w-4 shrink-0 text-primary" />
                          <span className="truncate font-normal">
                            {user?.first_name ||
                              user?.name ||
                              user?.telegram_username ||
                              user?.email?.split('@')[0] ||
                              'Пользователь'}
                          </span>
                          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[200px]">
                        {user && (
                          <div className="px-2 py-1.5 text-sm text-muted-foreground border-b">
                            {user.email ||
                              (user.telegram_username
                                ? `@${user.telegram_username}`
                                : 'Нет контакта')}
                            {user.name && ` (${user.name})`}
                          </div>
                        )}
                        <DropdownMenuItem onClick={() => router.push('/settings')}>
                          <Settings className="h-4 w-4" />
                          Настройки
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => window.open(SUPPORT_TELEGRAM_URL, '_blank')}
                        >
                          <MessageCircle className="h-4 w-4" />
                          Поддержка
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => window.open('https://t.me/rnp_4wb', '_blank')}
                        >
                          <ExternalLink className="h-4 w-4" />
                          Наш канал
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={handleLogout}
                          className="text-destructive focus:text-destructive"
                        >
                          <LogOut className="h-4 w-4" />
                          Выйти
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </>
            ) : (
              isHomeMode && (
                <div className="flex items-center gap-2">
                  <Link
                    href="/auth/register"
                    className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium text-muted-foreground shadow-sm transition-colors hover:bg-muted/60 hover:text-foreground"
                  >
                    Регистрация
                  </Link>
                  <Link
                    href="/auth/login"
                    className="rounded-lg border border-transparent bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground shadow-sm transition-opacity hover:opacity-90"
                  >
                    Войти
                  </Link>
                </div>
              )
            )}

            <div className="max-md:hidden">
              <ThemeToggle />
            </div>
          </div>
        </div>

        {/* Мобильная навигация: путь по разделам всегда виден, не только в выпадашке магазина */}
        {showBreadcrumbNav && (
          <div className="md:hidden border-t border-border/60 px-2 py-2 space-y-2">
            {renderBreadcrumbs('overflow-x-auto flex-nowrap pb-0.5 [-webkit-overflow-scrolling:touch]')}
          </div>
        )}
      </div>
    </header>
  )
}
